import { useEffect, useState } from 'react';
import { INDEX_DEFAUT, formatHandicap } from '../lib/me.ts';

/* Avatar, en trois temps.
 *
 *   1. la photo Google, si le compte est lié — c'est celle que la personne a
 *      choisie, elle prime ;
 *   2. Gravatar, tiré de l'adresse e-mail ;
 *   3. l'initiale dans un cercle.
 *
 * Gravatar est appelé avec d=404 : sans cela il renverrait son avatar
 * générique, et on ne pourrait plus distinguer « pas de Gravatar » de « un
 * Gravatar sans intérêt ». Le 404 déclenche onError, donc le repli sur
 * l'initiale.
 *
 * L'empreinte est en SHA-256, l'algorithme courant de Gravatar ; MD5 reste
 * accepté mais est déprécié depuis 2024.
 *
 * Les deux origines externes sont déclarées dans la CSP côté serveur
 * (img-src) : sans cela, le navigateur bloque les deux et tout le monde
 * hérite d'une initiale.
 */

/** La teinte est dérivée du texte : la même personne garde toujours la même
 *  couleur, sans qu'on ait à la stocker. */
const HUES = [150, 190, 260, 320, 20, 45, 95, 125];

function hueFor(seed: string): number {
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
    return HUES[Math.abs(h) % HUES.length]!;
}

function initialsOf(name: string, email?: string | null): string {
    const source = name.trim() || email?.trim() || '?';
    const words = source.replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
    if (words.length === 0) return '?';
    if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
    return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

/** Les adresses dont on SAIT qu'elles n'ont pas de Gravatar.
 *
 *  Le service répond 404 quand il n'a rien, et c'est voulu — c'est ce qui
 *  déclenche le repli sur l'initiale. Mais la réponse n'était retenue nulle
 *  part : chaque navigation repartait demander la même image à un tiers, pour
 *  se voir répondre la même chose. Mesuré : une requête perdue par écran, à
 *  vie, pour un joueur sans Gravatar — c'est-à-dire pour presque tous.
 *
 *  En mémoire du seul onglet, volontairement : un compte qui se crée un
 *  Gravatar doit pouvoir le voir apparaître en rouvrant l'application, pas
 *  attendre l'expiration d'un cache qu'on aurait écrit sur le disque. */
const SANS_GRAVATAR = new Set<string>();

async function gravatarUrl(email: string, sizePx: number): Promise<string | null> {
    // SubtleCrypto n'existe qu'en contexte sécurisé (HTTPS ou localhost). En
    // HTTP simple sur une IP de LAN il est absent : on saute Gravatar plutôt
    // que de planter.
    if (!globalThis.crypto?.subtle) return null;
    const normalized = email.trim().toLowerCase();
    if (SANS_GRAVATAR.has(normalized)) return null;
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(normalized));
    const hex = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
    return `https://www.gravatar.com/avatar/${hex}?s=${sizePx * 2}&d=404`;
}

interface Props {
    name: string;
    email?: string | null;
    avatarUrl?: string | null;
    /** Diamètre en pixels CSS. */
    size?: number;
    className?: string;
    /** L'index du joueur, affiché en pastille sous l'avatar.
     *
     *  Absent, la pastille ne paraît pas. Présent mais `null`, c'est un joueur
     *  qui n'a rien saisi : la pastille affiche 54, l'index d'entrée de la
     *  fédération, comme partout ailleurs. */
    handicap?: number | null;
}

/** En deçà, la pastille serait plus petite que le texte secondaire de
 *  l'application. Dehors, ganté, elle ne se lirait pas — et une pastille
 *  illisible est pire que pas de pastille : elle encombre sans informer. */
const TAILLE_MINI_POUR_PASTILLE = 40;

export function Avatar({ name, email, avatarUrl, size = 40, className = '', handicap }: Props) {
    const [src, setSrc] = useState<string | null>(avatarUrl ?? null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setFailed(false);
        if (avatarUrl) { setSrc(avatarUrl); return; }
        setSrc(null);
        if (email) {
            void gravatarUrl(email, size).then((u) => { if (!cancelled && u) setSrc(u); });
        }
        return () => { cancelled = true; };
    }, [avatarUrl, email, size]);

    const initials = initialsOf(name, email);
    const hue = hueFor(email || name);
    const style = { width: size, height: size } as const;
    const pastille = handicap !== undefined && size >= TAILLE_MINI_POUR_PASTILLE
        ? formatHandicap(handicap ?? INDEX_DEFAUT)
        : null;

    /* L'encre inversee : la pastille prend l'encre de la page en fond et son
     * papier en texte. Le contraste est maximal par construction, quelle que
     * soit la teinte de l'avatar dessous.
     *
     * L'anneau prend la couleur du fond de page et non celle de la carte : il
     * ne sert pas a fondre la pastille dans son support, mais a la decoller de
     * la photo qui pourrait avoir sa valeur exacte.
     *
     * Elle porte un role d'image et son libelle : lue seule, « 15,2 » ne veut
     * rien dire. */
    const Pastille = pastille === null ? null : (
        <span
            role="img"
            aria-label={`index ${pastille}`}
            className="pointer-events-none absolute -bottom-1 left-1/2 -translate-x-1/2
                       rounded-full bg-base-content px-1.5 font-bold leading-tight
                       tabular-nums text-base-100 ring-2 ring-base-100"
            style={{ fontSize: Math.max(11, Math.round(size * 0.26)) }}
        >
            {pastille}
        </span>
    );

    /* `aspect-square` et `overflow-hidden` en plus des dimensions.
     *
     * Les deux étaient posées en pixels, ce qui suffit tant que personne ne
     * contraint la boîte : dans une rangée flex, un parent qui étire ou un
     * demi-pixel de densité d'écran suffisait à rendre le cercle légèrement
     * ovale, et une photo de visage légèrement ovale se voit immédiatement.
     * Le rapport force le carré, le débordement caché force le disque, et
     * l'image ne peut plus dépasser de son masque. */
    /* Le disque est masqué, la pastille vit DEHORS.
     *
     * Elle déborde sous l'avatar : dans le même élément que le masque, elle
     * se faisait couper net par lui. D'où deux boîtes — l'enveloppe qui
     * positionne, le disque qui rogne. */
    /* `align-bottom` n'est pas de la cosmétique : sans lui, la boîte pend.
     *
     * L'enveloppe est de niveau LIGNE. Posée dans un bloc, elle crée une ligne
     * de texte, et cette ligne réserve sous la ligne de base la place des
     * jambages — six pixels de vide sous l'avatar.
     *
     * Avec une initiale, cela ne se voyait pas : la ligne de base d'un
     * conteneur flex est celle de son texte, située assez haut pour que la
     * boîte couvre déjà ses propres jambages. Avec une PHOTO, il n'y a pas de
     * texte : la ligne de base tombe au bas de l'image, les six pixels
     * s'ajoutent dessous, et un anneau arrondi à 9999 px sur une boîte de
     * 46×52 dessine une ellipse. C'est le fond de l'anneau qu'on voyait
     * déborder sous le visage.
     *
     * Aligner sur le bas de la ligne supprime la réservation. Dans une rangée
     * flex — le cas de presque tous les avatars de l'application — la
     * propriété est simplement ignorée. */
    return (
        <span className={`relative inline-flex shrink-0 align-bottom ${className}`} style={style}>
            <span className="aspect-square h-full w-full overflow-hidden rounded-full">
            {!src || failed ? (
                <span
                    className="inline-flex h-full w-full select-none items-center justify-center rounded-full font-semibold"
                    style={{
                        fontSize: Math.max(11, Math.round(size * 0.38)),
                        // Fond sombre teinte, texte clair de la meme famille :
                        // le contraste tient quelle que soit la teinte tiree.
                        backgroundColor: `oklch(38% 0.07 ${hue})`,
                        color: `oklch(92% 0.06 ${hue})`,
                    }}
                    aria-hidden="true"
                >
                    {initials}
                </span>
            ) : (
                <img
                    src={src}
                    alt=""
                    aria-hidden="true"
                    width={size}
                    height={size}
                    loading="lazy"
                    // referrerPolicy : Google refuse de servir la photo si
                    // l'en-tete Referer porte une origine qu'il ne reconnait pas.
                    referrerPolicy="no-referrer"
                    onError={() => {
                        // L'échec vaut réponse : inutile de redemander à chaque
                        // écran. Seuls les Gravatar sont retenus — une photo
                        // Google qui échoue peut tenir à un réseau coupé, et on
                        // ne veut pas la condamner pour la session.
                        if (src?.startsWith('https://www.gravatar.com/') && email) {
                            SANS_GRAVATAR.add(email.trim().toLowerCase());
                        }
                        setFailed(true);
                    }}
                    className="block h-full w-full rounded-full object-cover"
                />
            )}
            </span>
            {Pastille}
        </span>
    );
}
